import styled from 'styled-components'

import { Typography } from 'components/Typography/Typography'
import { useGlobalBanner } from 'utils/useGlobalBanner'
import { GLOBAL_BANNER_HEIGHT } from 'components/GlobalBanner/GlobalBanner'

const Page = styled.div<{ $hasBanner: boolean }>`
  min-height: calc(100vh - 462px);
  margin-top: calc(124px + ${p => (p.$hasBanner ? GLOBAL_BANNER_HEIGHT : 0)}px);

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-top: calc(100px + ${p => (p.$hasBanner ? GLOBAL_BANNER_HEIGHT : 0)}px);
  }
`

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 64px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 0 16px 48px;
  }
`

const PageTitle = styled(Typography)`
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Subtitle2}px;
  }
`

const Section = styled.div`
  margin-top: 40px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-top: 28px;
  }
`

const Paragraph = styled(Typography)`
  margin-top: 16px;
  line-height: 1.7;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Caption1}px;
  }
`

const BulletList = styled.ul`
  margin-top: 12px;
  padding-left: 24px;
`

const BulletItem = styled.li`
  margin-top: 8px;
  line-height: 1.7;
  color: ${p => p.theme.colors.text.primary};
  font-family: ${p => p.theme.typography.family.Poppins};
  font-size: ${p => p.theme.typography.size.Description}px;
  font-weight: ${p => p.theme.typography.weight.Regular};

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Caption1}px;
  }
`

const BulletItemBold = styled(BulletItem)`
  font-weight: ${p => p.theme.typography.weight.Medium};
`

export const PrivacyPolicy = () => {
  const { isEnabled: hasBanner } = useGlobalBanner()

  return (
    <Page $hasBanner={hasBanner}>
      <Container>
        <PageTitle variant="title2" fontWeight="bold">
          Privacy Policy
        </PageTitle>

        {/* 1. Introduction */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            1. Introduction
          </Typography>

          <Paragraph variant="description" fontWeight="medium">
            This Privacy Policy (the &quot;Privacy Policy&quot;) explains who we are, how and why we collect, use,
            disclose, and protect personal data, the legal bases we rely on, how long we retain such data, and the
            rights available to individuals under applicable data protection laws, including Regulation (EU) 2016/679
            (General Data Protection Regulation; hereinafter: the &quot;GDPR&quot;) and Croatian data protection laws.
          </Paragraph>

          <Paragraph variant="description">
            Kai Finance values your privacy and is committed to processing personal data lawfully, fairly, and
            transparently, in accordance with applicable data protection standards.
          </Paragraph>
        </Section>

        {/* 2. Who We Are */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            2. Who We Are
          </Typography>

          <Paragraph variant="description" fontWeight="medium">
            This Privacy Policy applies to the processing of personal data by Kai Finance (&quot;Kai Finance&quot;,
            &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) in connection with the provision of our Services, as
            defined in our Terms of Service. For the purposes of the GDPR, Kai Finance acts as the data controller in
            relation to the personal data described in this Privacy Policy.
          </Paragraph>

          <Paragraph variant="description" fontWeight="medium">
            In this Privacy Policy, &quot;you&quot;, &quot;your&quot;, and &quot;individual&quot; mean any natural
            person who accesses or uses our Services. If you have any questions regarding this Privacy Policy or the
            processing of your personal data, you may contact us at: kai@kunalabs.io.
          </Paragraph>
        </Section>

        {/* 3. Categories of Personal Data We Collect */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            3. Categories of Personal Data We Collect
          </Typography>

          <Paragraph variant="description">
            We collect and process only limited categories of personal data, depending on how you interact with us. The
            following data may be collected and processed:
          </Paragraph>

          <BulletList>
            <BulletItemBold>
              Wallet and On-Chain Data: When you access or use our Services, we process your wallet address and related
              publicly available blockchain transaction data as necessary to provide the Services requested under our
              Terms of Service. Blockchain data is inherently public and recorded on decentralized networks. Such data
              cannot be modified or deleted by us.
            </BulletItemBold>
            <BulletItemBold>
              Telegram Notification Data: If you voluntarily connect your Telegram account to the Kai Finance
              notification bot, we collect and store your Telegram ID with your explicit consent. The purpose of this
              processing is to send alerts.
            </BulletItemBold>
            <BulletItemBold>
              Communication Data: If you contact us by email or otherwise engage with support, we may process your email
              address and the content of your communication in order to respond to your inquiry.
            </BulletItemBold>
            <BulletItemBold>
              Marketing Data: If you subscribe to newsletters or promotional communications, we may process your email
              address and subscription preferences in order to send you marketing materials, updates, and related
              communications, subject to your prior consent.
            </BulletItemBold>
          </BulletList>

          <Paragraph variant="description">
            We do not intentionally collect special categories of personal data within the meaning of the GDPR.
          </Paragraph>
        </Section>

        {/* 4. How We Collect Personal Data */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            4. How We Collect Personal Data
          </Typography>

          <Paragraph variant="description">We collect personal data directly from you when:</Paragraph>

          <BulletList>
            <BulletItem>you use or interact with our Services;</BulletItem>
            <BulletItem>you connect your Telegram account to our notification bot;</BulletItem>
            <BulletItem>you contact us via email or other communication channels;</BulletItem>
            <BulletItem>you subscribe to marketing communications;</BulletItem>
            <BulletItem>you engage with us through social media.</BulletItem>
          </BulletList>

          <Paragraph variant="description">
            Where processing is based on consent, you provide that consent through clear affirmative action, such as
            selecting relevant options or subscribing to communications. Consent may be withdrawn at any time.
          </Paragraph>

          <Paragraph variant="description">
            When you interact with us through social media channels or other third-party service providers, your
            interaction and any processing of your personal data by those providers will be governed by their respective
            privacy policies.
          </Paragraph>
        </Section>

        {/* 5. Legal Bases for Processing */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            5. Legal Bases for Processing
          </Typography>

          <Paragraph variant="description">
            Depending on the nature of your interaction with our Services and the specific purpose of the processing, we
            rely on one or more of the following legal grounds:
          </Paragraph>

          <BulletList>
            <BulletItemBold>
              Performance of Terms of Service: Processing of wallet addresses and related technical data is necessary to
              provide the requested Services.
            </BulletItemBold>
            <BulletItemBold>
              Consent: Telegram notifications and marketing communications are processed based on your explicit consent.
              You may withdraw consent at any time without affecting the lawfulness of processing carried out prior to
              withdrawal.
            </BulletItemBold>
            <BulletItemBold>
              Legal Obligation: We may process personal data where necessary to comply with applicable legal or
              regulatory obligations, including responding to data subject rights requests.
            </BulletItemBold>
            <BulletItemBold>
              Legitimate Interests: We may process personal data where necessary for our legitimate interests, such as
              responding to general inquiries, preventing misuse, or defending legal claims, provided such interests do
              not override your fundamental rights and freedoms.
            </BulletItemBold>
          </BulletList>
        </Section>

        {/* 6. How We Use Personal Data */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            6. How We Use Personal Data
          </Typography>

          <Paragraph variant="description">
            We may use personal data for the following purposes: (i) to provide our Services; (ii) to enable optional
            Telegram notifications; (iii) to respond to support or privacy-related inquiries; (iv) to send newsletters,
            marketing communications, and updates where consent has been provided; (v) to comply with legal and
            regulatory obligations; (vi) to ensure the security, integrity, and proper functioning of the Interface.
          </Paragraph>

          <Paragraph variant="description">
            We do not use personal data for profiling, automated decision-making, or behavioural advertising. We do not
            use cookies, analytics trackers, or similar technologies for profiling, targeted advertising, or analytics
            purposes. If such technologies are introduced in the future, we will update this Privacy Policy and, where
            required, obtain your consent in accordance with applicable laws.
          </Paragraph>
        </Section>

        {/* 7. Sharing of Personal Data */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            7. Sharing of Personal Data
          </Typography>

          <Paragraph variant="description">
            We may disclose personal data only in the following circumstances:
          </Paragraph>

          <BulletList>
            <BulletItemBold>
              Service Providers: We may share personal data with trusted third-party service providers that support the
              provision of our Services, including cloud hosting, infrastructure, and monitoring providers (such as AWS,
              Cloudflare, Grafana Cloud, etc.). These entities act as data processors and process personal data
              according to the Data Processing Agreements and applicable laws.
            </BulletItemBold>
            <BulletItemBold>
              Legal and Regulatory Disclosure: We may disclose personal data to courts, regulatory authorities, law
              enforcement bodies, or other public authorities where required by applicable law or where necessary to
              establish, exercise, or defend legal claims.
            </BulletItemBold>
            <BulletItemBold>
              Consent-Based Disclosure: We may disclose personal data to other third parties where you have expressly
              consented to such disclosure.
            </BulletItemBold>
          </BulletList>
        </Section>

        {/* 8. International Transfers */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            8. International Transfers
          </Typography>

          <Paragraph variant="description">
            Some of our service providers may process personal data outside the European Economic Area (EEA). Where
            personal data is transferred to countries not subject to an adequacy decision by the European Commission, we
            implement appropriate safeguards in accordance with the GDPR, including Standard Contractual Clauses (SCCs)
            and supplementary technical and organizational measures such as encryption and strict access controls.
          </Paragraph>
        </Section>

        {/* 9. Data Retention */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            9. Data Retention
          </Typography>

          <Paragraph variant="description">
            We retain personal data only for as long as necessary to fulfil the purposes for which it was collected or
            to comply with legal obligations. Retention periods depend on the type of data and the purpose of
            processing:
          </Paragraph>

          <BulletList>
            <BulletItem>
              Wallet and blockchain transaction data are processed for the duration of your use of the Services.
              Blockchain data is publicly recorded on decentralized networks and cannot be altered or deleted by us.
            </BulletItem>
            <BulletItem>
              Telegram IDs and notification data are processed on the basis of your consent and retained for as long as
              the notification service is active. If you withdraw consent or deactivate the bot, the data is deleted
              without undue delay.
            </BulletItem>
            <BulletItem>
              Email communications and support requests are retained for up to 24 months after resolution of the
              inquiry. Where correspondence relates to potential disputes, relevant data may be retained for up to 5
              years.
            </BulletItem>
            <BulletItem>Marketing data is retained until you withdraw your consent.</BulletItem>
          </BulletList>

          <Paragraph variant="description">
            Upon expiry of the applicable retention period, personal data is securely deleted or irreversibly
            anonymised, unless retention is required by law (e.g. tax or accounting obligations), necessary for the
            establishment, exercise, or defence of legal claims, or technically impossible to delete.
          </Paragraph>
        </Section>

        {/* 10. Security Measures */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            10. Security Measures
          </Typography>

          <Paragraph variant="description">
            We implement appropriate technical, physical, and organizational measures to protect personal data against
            accidental or unlawful destruction, loss, alteration, unauthorised disclosure, or access. Access to personal
            data is restricted to authorised personnel who are bound by confidentiality obligations and may process such
            data only in accordance with our instructions. While we maintain industry-standard security safeguards, no
            system can be guaranteed to be completely secure.
          </Paragraph>
        </Section>

        {/* 11. Personal Data Breach */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            11. Personal Data Breach
          </Typography>

          <Paragraph variant="description">
            If a personal data breach is suspected, we promptly investigate and take immediate steps to contain the
            incident and mitigate potential harm. We assess the nature of the breach, the categories and volume of data
            affected, and the potential impact on individuals.
          </Paragraph>

          <Paragraph variant="description">
            Where required, we notify the competent supervisory authority without undue delay and, where feasible,
            within 72 hours of becoming aware of the breach. If a breach is likely to result in a high risk to
            individuals&apos; rights and freedoms, we will also notify affected individuals without undue delay.
          </Paragraph>

          <Paragraph variant="description">
            All breaches are documented internally, including the facts relating to the breach, its effects, and the
            remedial actions taken.
          </Paragraph>
        </Section>

        {/* 12. Your Rights */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            12. Your Rights
          </Typography>

          <Paragraph variant="description">
            You have a number of important rights regarding the personal data we process about you under applicable data
            protection laws. A summary of these rights is provided below for your reference.
          </Paragraph>

          <BulletList>
            <BulletItemBold>
              Right of Access: You have the right to request confirmation as to whether we process personal data about
              you and, if so, to obtain access to that data. This includes the right to receive information about the
              purposes of processing, the categories of data concerned, the recipients or categories of recipients to
              whom the data has been disclosed, and the envisaged retention period.
            </BulletItemBold>
            <BulletItemBold>
              Right to Rectification: You have the right to request the correction of inaccurate personal data and to
              have incomplete personal data completed.
            </BulletItemBold>
            <BulletItemBold>
              Right to Erasure (&quot;Right to Be Forgotten&quot;): You have the right to request the deletion of
              personal data where, for example, the data is no longer necessary for the purposes for which it was
              collected, where you withdraw consent and no other legal basis applies, or where processing is unlawful.
              Please note that this right does not apply to blockchain data that is publicly recorded and beyond our
              technical control.
            </BulletItemBold>
            <BulletItemBold>
              Right to Restriction of Processing: You have the right to request that we restrict the processing of your
              personal data in certain circumstances, including where you contest its accuracy or object to its
              processing.
            </BulletItemBold>
            <BulletItemBold>
              Right to Object: You have the right to object to the processing of your personal data where such
              processing is based on legitimate interests or where it is used for direct marketing purposes.
            </BulletItemBold>
            <BulletItemBold>
              Right to Data Portability: Where processing is based on your consent or on the performance of the Terms of
              Service, you have the right to receive the personal data you provided to us in a structured, commonly
              used, and machine-readable format and to request its transmission to another controller, where technically
              feasible.
            </BulletItemBold>
            <BulletItemBold>
              Right to Withdraw Consent: Where processing is based on consent, you may withdraw that consent at any
              time. Withdrawal does not affect the lawfulness of processing carried out prior to withdrawal.
            </BulletItemBold>
            <BulletItemBold>
              Right to Lodge a Complaint: If you believe that your personal data has been processed in violation of
              applicable law, you have the right to lodge a complaint with a supervisory authority. In Croatia, the
              competent authority is the Croatian Personal Data Protection Agency (Cro. AZOP), or you may contact your
              local EU supervisory authority.
            </BulletItemBold>
          </BulletList>

          <Paragraph variant="description">You may exercise your rights by contacting us at kai@kunalabs.io.</Paragraph>
        </Section>

        {/* 13. Changes to the Privacy Policy */}
        <Section>
          <Typography variant="label1" fontWeight="semiBold">
            13. Changes to the Privacy Policy
          </Typography>

          <Paragraph variant="description">
            We may amend or update this Privacy Policy from time to time to reflect changes in our data processing
            practices, the Services, or applicable law. When we make material changes, we may provide additional notice
            through the Interface or by other appropriate means. Any updated Privacy Policy will become effective upon
            publication at https://kai.finance, unless otherwise stated.
          </Paragraph>
        </Section>
      </Container>
    </Page>
  )
}
